/**
 * RFC 5322 address-list parsing, and the three normalisations the address
 * tables are keyed on.
 *
 * A HAND-WRITTEN SCANNER, NOT A REGEX, for one reason that a regex cannot be
 * talked out of: `"Smith, John" <j@x.example>` is ONE address, and
 * `Undisclosed recipients:;` is NONE. Commas are separators except inside
 * quotes, angle brackets are terminators except inside comments, and comments
 * nest — three context-sensitive rules, which is precisely what a regular
 * language cannot express. Split on `,` and every `"Surname, First"` and
 * `"Firma GmbH, Berlin"` header becomes two junction rows, one of them holding
 * a fragment that is not an address; every regex that tries to patch that turns
 * into a second, worse scanner.
 *
 * The scanner deliberately never throws. lex.ts throws QueryError because the
 * user typed the query and can fix it; nothing here is fixable by anyone — a
 * header with an unterminated quote is what a 2004 mail server sent, and the
 * only useful response is to take the address and move on.
 *
 * WHAT WE FOUND ALREADY IN THE REPO, and reused rather than duplicated:
 *   - identity/index.ts exports `stripPlusDetail`, which is exactly the
 *     plus-addressing rule `base` needs; `normaliseAddress` calls it.
 *   - identity/index.ts has a private `bare()` that pulls an address out of
 *     angle brackets and lowercases it; it is four lines and not exported, so
 *     `normaliseAddress` does that inline.
 *   - addresses/masked.ts is the masked-alias state machine and `sameSite`
 *     domain comparison. NO parsing, NO folding, NO encoded-word handling —
 *     nothing there to reuse for this file.
 */

import { stripPlusDetail } from '../identity/index.ts'
import { foldForSearch } from '../search/fold.ts'
import { decodeEncodedWords } from './rfc2047.ts'

export interface ParsedAddress {
  /**
   * The display name EXACTLY as the header carried it, still holding any
   * encoded words. Run it through `displayName` before storing or rendering —
   * see the decode-after-splitting note in rfc2047.ts for why this order.
   */
  readonly name: string
  /** The addr-spec, un-angled but otherwise untouched. `normaliseAddress` lowercases. */
  readonly address: string
}

export interface NormalisedAddress {
  /** Lowercased addr-spec: the `addresses.addr` unique key. */
  readonly addr: string
  /** Everything after the last `@`, lowercased. `''` when there is no `@`. */
  readonly domain: string
  /** `addr` with plus-addressing removed. Widens `to:alice@x` to `alice+petco@x`. */
  readonly base: string
}

/** The cap on `address_identities.name`; the column is pinned to it in the schema. */
export const MAX_DISPLAY_NAME = 200

function collapse(s: string): string {
  return s.trim().replace(/\s+/gu, ' ')
}

/**
 * Reads a quoted-string, honouring `\"` and `\\`. Unlike lex.ts's version it
 * cannot throw, and it REPORTS an unterminated quote rather than swallowing the
 * rest of the header: `"unterminated <a@x.example>` still has to yield its
 * address, and consuming to end-of-input would eat the angle brackets and lose
 * it. The caller falls back to treating the opening `"` as an ordinary
 * character, which leaves a stray quote in the display name — a faithful
 * reflection of a broken header, and the address is what the junction needs.
 */
function readQuoted(input: string, start: number): { value: string; next: number; closed: boolean } {
  let out = ''
  let i = start + 1
  while (i < input.length) {
    const ch = input[i]!
    if (ch === '\\' && i + 1 < input.length) {
      out += input[i + 1]
      i += 2
      continue
    }
    if (ch === '"') return { value: out, next: i + 1, closed: true }
    out += ch
    i++
  }
  return { value: out, next: i, closed: false }
}

/**
 * Turn one accumulated mailbox into a result, or null if it holds no address —
 * which is how `Undisclosed recipients:;`, a trailing comma and `<>` all come
 * back as nothing without a special case for any of them.
 */
function finish(display: string, angle: string | null, comment: string): ParsedAddress | null {
  const text = collapse(display)
  const note = collapse(comment)

  if (angle !== null) {
    const address = angle.trim()
    if (address === '') return null
    // `a@x (John)` puts the name in a comment. Still seen from Unix mailers.
    return { name: text || note, address }
  }

  // No angle brackets. Either a bare `alice@example.com`, or the illegal but
  // common `Alice alice@example.com`. Take the LAST whitespace-run containing
  // an `@` as the address and treat everything else as the name.
  const parts = text.split(' ').filter((p) => p !== '')
  let at = -1
  for (let i = parts.length - 1; i >= 0; i--) {
    if (parts[i]!.includes('@')) {
      at = i
      break
    }
  }
  if (at < 0) return null
  const rest = [...parts.slice(0, at), ...parts.slice(at + 1)].join(' ')
  return { name: rest || note, address: parts[at]! }
}

/**
 * Parse a From/To/Cc/Bcc header value into its mailboxes.
 *
 * Returns `[]` for null, undefined, empty, and for a group with no members.
 * Never throws.
 */
export function parseAddressList(raw: string | null | undefined): ParsedAddress[] {
  if (raw === null || raw === undefined || raw === '') return []

  const out: ParsedAddress[] = []
  let display = ''
  let angle = ''
  let hasAngle = false
  let comment = ''
  let inAngle = false
  let commentDepth = 0

  /**
   * THE UNQUOTED COMMA. `Weber, Anna <anna@y.example>` — the shape Exchange and
   * Outlook emit constantly, and the one this file's header comment names as
   * the reason it is a scanner and not a regex. The quoted form
   * (`"Weber, Anna" <…>`) has always worked; the unquoted one used to lose the
   * surname outright, because the comma flushed `Weber`, `finish` found no `@`
   * in it and returned null, and the identity was stored as `Anna` alone. A
   * surname is the commonest thing a person types, so `from:weber` came back
   * empty where the pre-S2 LIKE over the raw header found the message — a false
   * negative on a value lying WHOLLY INSIDE a display name, which is precisely
   * what G1 says cannot happen.
   *
   * The recovery is the standard obs-phrase one: hold a fragment that yields no
   * address and is plainly the front half of a name — no angle brackets, no
   * `@`, not empty — and put it back in front of the next fragment that does
   * yield one, with the separator that split them. Reinserting the separator
   * rather than a space is what makes the stored `name` the header's own text,
   * so `from:weber`, `from:anna` and `from:"weber, anna"` all match it.
   *
   * It cannot be told apart from `Undisclosed, alice@x.example`, and it is not
   * meant to be: there is no signal in the header that distinguishes them, and
   * of the two readings the surname is overwhelmingly the common one. A
   * fragment that never finds a following address is dropped, exactly as before.
   */
  let pending = ''
  let pendingSep = ','

  const flush = (sep: string): void => {
    const parsed = finish(display, hasAngle ? angle : null, comment)
    if (parsed === null) {
      const text = collapse(display)
      if (!hasAngle && text !== '' && !text.includes('@')) {
        pending = pending === '' ? text : `${pending}${pendingSep} ${text}`
        pendingSep = sep
      }
    } else {
      out.push(
        pending === ''
          ? parsed
          : { name: parsed.name === '' ? pending : `${pending}${pendingSep} ${parsed.name}`, address: parsed.address },
      )
      pending = ''
    }
    display = ''
    angle = ''
    hasAngle = false
    comment = ''
    inAngle = false
  }

  let i = 0
  while (i < raw.length) {
    const ch = raw[i]!

    // Comments nest: `(a (b) c)` is one comment, and the inner `)` does not end
    // it. A comment inside angle brackets is dropped rather than kept as a name.
    if (commentDepth > 0) {
      if (ch === '\\' && i + 1 < raw.length) {
        if (!inAngle) comment += raw[i + 1]
        i += 2
        continue
      }
      if (ch === '(') {
        commentDepth++
        if (!inAngle) comment += ch
      } else if (ch === ')') {
        commentDepth--
        if (commentDepth > 0 && !inAngle) comment += ch
      } else if (!inAngle) {
        comment += ch
      }
      i++
      continue
    }

    if (ch === '(') {
      commentDepth = 1
      if (!inAngle && comment !== '') comment += ' '
      i++
      continue
    }

    if (ch === '"') {
      // The whole reason this is a scanner: a comma in here is not a separator.
      const { value, next, closed } = readQuoted(raw, i)
      if (!closed) {
        // Unmatched. Demote it to a literal and carry on, so the `<...>` further
        // along the line is still seen.
        display += ch
        i++
        continue
      }
      if (inAngle) angle += value
      else display += value
      i = next
      continue
    }

    if (ch === '<' && !inAngle) {
      inAngle = true
      hasAngle = true
      angle = ''
      i++
      continue
    }

    if (ch === '>' && inAngle) {
      inAngle = false
      i++
      continue
    }

    if (!inAngle) {
      if (ch === ',') {
        flush(',')
        i++
        continue
      }
      if (ch === ';') {
        // End of an RFC 5322 group. Flushes whatever member is still open.
        flush(';')
        i++
        continue
      }
      if (ch === ':' && !hasAngle && !display.includes('@')) {
        // Group syntax: everything before the colon was the group's name, not a
        // mailbox. Guarded on "no address material seen yet" so a colon inside
        // an unquoted display name (`Support: Tickets <t@x>` — illegal, but it
        // happens) costs at most the fragment before it, never the address.
        display = ''
        comment = ''
        i++
        continue
      }
    }

    if (inAngle) angle += ch
    else display += ch
    i++
  }
  // End of input: there is no separator and nothing after it, so a fragment
  // still pending here is dropped, which is what happened to it before.
  flush('')
  return out
}

/**
 * Lowercase an addr-spec and derive the two keys the address tables index on.
 */
export function normaliseAddress(addr: string): NormalisedAddress {
  // toLowerCase, NOT toLocaleLowerCase. The locale-sensitive form maps `I` to
  // dotless `ı` under a Turkish locale, so the very same header would produce a
  // different `addresses.addr` — and therefore a different unique key and a
  // different row — depending on which machine ran the ingest. The index must
  // be machine-independent; ingest runs on the VPS and on this laptop both.
  const bare = (/<([^>]+)>/.exec(addr)?.[1] ?? addr).trim().toLowerCase()
  const at = bare.lastIndexOf('@')
  return {
    addr: bare,
    domain: at < 0 ? '' : bare.slice(at + 1),
    // Reuses identity/index.ts's rule rather than restating it; it is idempotent
    // on an already-bare lowercased address.
    base: stripPlusDetail(bare),
  }
}

/**
 * The string `/suggest` and the message list RENDER. Keeps its case and its
 * diacritics — `Grüße Anna` must come back with the umlaut, which is why this
 * is a separate function from `displayNameKey` and why the schema has separate
 * `name` and `name_fold` columns.
 */
export function displayName(raw: string | null | undefined): string {
  if (raw === null || raw === undefined) return ''
  const text = collapse(decodeEncodedWords(raw))
  if (text.length <= MAX_DISPLAY_NAME) return text
  // Never cut between a surrogate pair: SQLite would store a lone surrogate and
  // every later read of that row would render a replacement character.
  const head = text.charCodeAt(MAX_DISPLAY_NAME - 1)
  const end = head >= 0xd800 && head <= 0xdbff ? MAX_DISPLAY_NAME - 1 : MAX_DISPLAY_NAME
  return text.slice(0, end)
}

/**
 * What `address_identities.name_fold` stores and what every name predicate
 * matches. TWO FUNCTIONS ON PURPOSE: a single `normaliseDisplayName` would have
 * to return both strings or silently pick one, and the column that got picked
 * would be wrong in the UI or wrong in the index.
 */
export function displayNameKey(raw: string | null | undefined): string {
  return foldForSearch(displayName(raw))
}
